import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, of } from 'rxjs';
import { ErrorStateService } from './error-state.service';
import
{
  GmrAuthResponse
, GmrFunction
, GmrFunctionParameter
, GmrLovResponse
, GmrLovRow
, GmrPresetResponse
, GmrUnavailableStats
, GmrUserInfo
} from './gmr.models';
import { groupBy } from '../shared/group-by.util';
import { PresetResultSetName, findContractViolations } from './preset-contract';
import { HashStateService } from './hash-state.service';
import { IwidCookieService } from './iwid-cookie.service';
import { LoadingService } from './loading.service';
import { sanitisedDownloadUrl } from './matomo.service';
import { ThirdPartyIntegrationsService } from './third-party-integrations.service';

//
// 1:1 port of the data-fetching half of the AngularJS 'functions' controller (gmr-app.js):
// startFunction, setLovPreset, lovSelectionChanged, lovTextChanged. The UI-orchestration half
// of startParameters (date-picker fallback, title juggling, focus-first-input, hash-restore) is
// handled by ParameterFormComponent, which reads/writes these same signals.
//
@Injectable({ providedIn: 'root' })
export class GmrDataService
{
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);
  private readonly loading = inject(LoadingService);
  private readonly errorState = inject(ErrorStateService);
  private readonly thirdParty = inject(ThirdPartyIntegrationsService);
  private readonly iwidCookie = inject(IwidCookieService);
  private readonly hashState = inject(HashStateService);

  readonly myFunctions = signal<GmrFunction[]>([]);
  readonly myParameters = signal<GmrFunctionParameter[]>([]);
  readonly myInfo = signal<GmrUserInfo | null>(null);
  readonly unavailableStats = signal<GmrUnavailableStats | null>(null);

  readonly selectedFunction = signal<GmrFunction | null>(null);
  readonly selectedFunctionParameters = signal<GmrFunctionParameter[]>([]);

  //
  // 1:1 port of `myFunctions | groupBy: 'can_description_i18n'`, shared by both the always-visible
  // sidebar and the filterable step1 list so the grouping itself isn't computed twice.
  //
  readonly groupedFunctions = computed(() => groupBy(this.myFunctions(), fn => fn.can_description_i18n as string));

  //
  // Indexed the same way as the original $scope.myLovPresets: by the index of the parameter
  // within the currently selected function's parameter list, not by parameter identity.
  //
  readonly myLovPresets = signal<GmrLovRow[][]>([]);

  startFunction(): void
  {
    const url = 'auth?preset=gmr-functions&returnUrl=' + encodeURIComponent(window.location.href);

    this.loading.track(this.http.get<GmrAuthResponse>(url)).pipe(
      catchError(error => this.handleFallbackError(error, 'xxgmr116'))
    ).subscribe(authResponse =>
    {
      if (!authResponse) return;

      if (authResponse.isAuthenticated)
      {
        this.loading.track(this.http.get<GmrPresetResponse>('Preset?preset=gmr-functions')).pipe(
          catchError(error => this.handleFallbackError(error, 'xxgmr117'))
        ).subscribe(response =>
        {
          if (response) this.handlePresetResponse(response);
        });
      }
      else
      {
        window.location.href = authResponse.authenticationUrl;
      }
    });
  }

  private handlePresetResponse(response: GmrPresetResponse): void
  {
    //
    // The catalog has arrived; whatever it turns out to contain, the loading overlay has no reason to
    // stay up. Set before the validity check below, which itself ends in an error modal.
    //
    this.loading.awaitingCatalog.set(false);

    const results = response.Results;

    //
    // `unavailable_stats` joined this guard on 2026-08-14. It was dereferenced a few lines below
    // without ever being checked, so a response missing that one result set threw a TypeError instead
    // of reaching the error modal the other four already had.
    //
    if (!results.functions || !results.function_parameters || !results.context_settings || !results.system_users || !results.unavailable_stats)
    {
      this.translate.get(['itgen_gmr_error', 'itgen_gmr_invalid_answer_from_server']).subscribe(t =>
      {
        this.errorState.showFromHttpError(t['itgen_gmr_invalid_answer_from_server'], 'xxgmr114',
        {
          itgen_gmr_error: t['itgen_gmr_error']
        , itgen_gmr_failed_data_retrieval: ''
        , itgen_gmr_reauthenticate: ''
        });
      });
      return;
    }

    const myFunctions = results.functions.Data;
    const myParameters = results.function_parameters.Data;
    const myInfo = results.system_users.Data[0];
    const unavailableStats = results.unavailable_stats.Data[0];

    //
    // Verify the field names before a single one of them is read, and stop if they are wrong. Fatal on
    // the maintainer's instruction of 2026-08-14, over the reporting-only variant this first shipped
    // with: a field that reads as `undefined` produces an application that looks like it works and is
    // quietly wrong - a cookie without its keys, a badge that never appears - and that is worse to
    // ship than one that refuses to start and says why. The loading overlay is already down (top of
    // this method), so what the user gets is the error modal and nothing behind it.
    //
    if (!this.verifyContract(
    [ ['system_users', myInfo]
    , ['functions', myFunctions[0]]
    , ['function_parameters', myParameters[0]]
    , ['unavailable_stats', unavailableStats]
    ]))
    {
      return;
    }

    for (const fn of myFunctions)
    {
      fn.cfn_description = cleanupResourceText(fn.cfn_description as string);
      if (fn.cfn_documentation != null)
      {
        fn.cfn_documentation = cleanupResourceText(fn.cfn_documentation as string);
      }
      fn.can_description = (fn.can_description as string)
        .replace(/res:/g, '')
        .replace(/\{|\}|/g, '')
        .replace(/: /g, '_')
        .replace(/ /g, '_')
        .toLowerCase();
    }

    for (const param of myParameters)
    {
      param.cfr_description = (param.cfr_description as string)
        .replace(/res:/g, '')
        .replace(/\{|\}|\(|\)/g, '')
        .replace(/ - /g, '_')
        .replace(/ /g, '_');

      if (param.cfr_user_hint != null)
      {
        param.cfr_user_hint = cleanupResourceText(param.cfr_user_hint as string);
      }
    }

    this.myFunctions.set(myFunctions);
    this.myParameters.set(myParameters);
    this.myInfo.set(myInfo);
    this.unavailableStats.set(unavailableStats);

    //
    // 1:1 port of `$translate.use($scope.myInfo.usr_language.substring(0, 2));` - the <select>'s
    // "selected" default in the template is only ever a pre-data-load visual placeholder, the
    // actual active language is driven by the user's own language once it's known.
    //
    this.translate.use(myInfo.usr_language.substring(0, 2));

    if (unavailableStats.available_cnt === 0)
    {
      //
      // Companies exist but every one is blocked for compliance: asking for a company to become
      // available cannot help, so say what is going on and where to turn.
      //
      const complianceOnly = unavailableStats.compliance_cnt > 0
                             && unavailableStats.inactive_cnt === 0
                             && unavailableStats.outside_service_area_cnt === 0
                             && unavailableStats.unknown_cnt === 0
                             ;

      this.translate.get(['itgen_gmr_error', 'itgen_gmr_failed_data_retrieval', 'itgen_gmr_reauthenticate', 'itgen_gmr_120_msg', 'itgen_gmr_124_msg', 'itgen_gmr_124_hint']).subscribe(t =>
      {
        if (complianceOnly)
        {
          this.errorState.showFromHttpError(t['itgen_gmr_124_msg'], 'xxgmr124', t as any, t['itgen_gmr_124_hint']);
        }
        else
        {
          this.errorState.showFromHttpError(t['itgen_gmr_120_msg'], 'xxgmr120', t as any);
        }
      });
    }

    const iwid = this.iwidCookie.resolveAndPersist(myInfo);

    //
    // The original gated vendor SDK init behind a 1s setTimeout. Part of that was an AngularJS-digest
    // workaround that no longer applies with signals; the rest was an attempt to wait out the
    // asynchronously injected SDKs, and it did not work - a fixed delay cannot win a race whose other
    // side has no fixed duration, so whatever had not arrived within the second was skipped for the
    // whole session.
    //
    // The wait now sits where the uncertainty is: Inspectlet is queued in ThirdPartyIntegrationsService,
    // and its snippet replays anything pushed before the SDK lands. This call therefore no longer needs
    // to be deferred at all, and the identity reaches the vendors a second earlier for it.
    //
    this.thirdParty.initAll(myInfo, iwid);
  }

  //
  // Every distinct violation already reported in this page's lifetime. Without it the LOV check below
  // would raise the same modal on every dropdown that is opened, and a fault that is reported forty
  // times reads as forty faults. It suppresses the *reporting* only - never the verdict, which is
  // computed from the violations found on this call. A second dropdown with the same broken contract
  // must still be refused, whether or not its message has been on screen before.
  //
  private readonly reportedContractViolations = new Set<string>();

  //
  // Runs the field-name contract over a set of (result set, row) pairs. Returns true when every row
  // honours it, false when anything was wrong - in which case the caller must abandon the data.
  // See preset-contract.ts for what is checked and why it can be checked at all.
  //
  private verifyContract(rows: readonly (readonly [PresetResultSetName, unknown])[]): boolean
  {
    const violations = rows.flatMap(([resultSetName, row]) => findContractViolations(resultSetName, row));

    if (violations.length === 0)
    {
      return true;
    }

    const unreported = violations.filter(violation => !this.reportedContractViolations.has(violation));

    if (unreported.length === 0)
    {
      return false;
    }

    for (const violation of unreported)
    {
      this.reportedContractViolations.add(violation);
    }

    const message = unreported.join(' ');

    //
    // Written to the console first and synchronously. The modal below goes through TranslateService,
    // which needs a locale file to have arrived; the console line does not, and this is the one fault
    // that can plausibly coincide with a half-loaded application. Since the verdict is fatal, the
    // console is also the only place a developer can read the full list if the modal is dismissed.
    //
    console.error('xxgmr122: ' + message);

    this.translate.get('itgen_gmr_error').subscribe(errorLabel =>
    {
      //
      // Deliberately not translated. It names JSON field names and their casing, its readers are
      // whoever maintains the SQL template or the frontend, and six locale files for a message no
      // customer can act on is cost without a reader. The title around it is translated, as
      // everywhere else. showFromHttpError also forwards it to Matomo/Inspectlet, so a violation
      // that reaches a real user is reported without anyone having to notice the modal.
      //
      this.errorState.showFromHttpError(message, 'xxgmr122',
      {
        itgen_gmr_error: errorLabel
      , itgen_gmr_failed_data_retrieval: ''
      , itgen_gmr_reauthenticate: ''
      });
    });

    return false;
  }

  //
  // 1:1 port of the data half of $scope.startParameters. Returns true if the parameter form
  // should be shown, false if this function has no parameters and was navigated to directly
  // (window.location = "Preset?preset=..."). The Inspectlet "badge-downloaded report"
  // tracking fires in BOTH cases in the original (unconditionally, after the if/else) - distinct
  // from the separate "badge-navigated to report" event UiStateService.goToParameterForm reports,
  // which only fires from the .goParam click handler.
  //
  // That unconditional placement is not reproduced, because it is wrong rather than merely odd:
  // opening a parameter form is not a download, and reporting it as one made every parameterised
  // report look as though it were downloaded the instant it was opened - alongside the "opened"
  // event for the very same click. A download now reports itself where downloads actually happen,
  // in DownloadService; the branch below is the one case where selecting the report IS the
  // download, since it has no parameters to fill in and goes straight to the file.
  //
  selectFunction(fn: GmrFunction): boolean
  {
    let shown = false;

    if (fn.cfrstat_cnt)
    {
      const functionParameters = this.myParameters().filter(p => p.cfn_code === fn.cfn_code);

      for (const param of functionParameters)
      {
        if (param.cfr_default_value && param.cfr_code === 'division_codes')
        {
          param.cfr_default_value_arr = [param.cfr_default_value as string];
        }
      }

      this.selectedFunction.set(fn);
      this.selectedFunctionParameters.set(functionParameters);
      this.myLovPresets.set(new Array(functionParameters.length));

      if (functionParameters.length !== fn.cfrstat_cnt)
      {
        const message = 'Mismatch in parameter count.';
        this.thirdParty.reportGenericError(message, 'xxgmr112');
        alert('xxgmr112: ' + message);
      }

      functionParameters.forEach((param, index) =>
      {
        const urlDefinedValue = this.hashState.formValue(param.cfr_code);

        //
        // 1:1 port of the value-restoration branches in startParameters, EXCEPT for date fields:
        // the original converts to a JS Date because AngularJS' own input[type=date] directive
        // knows how to turn a bound Date back into the yyyy-mm-dd string a native date input
        // requires. Angular's plain NgModel on a native <input type="date"> has no such
        // conversion - handing it a Date object just fails silently (the browser rejects the
        // non-ISO-string value and the field renders empty) - so this keeps date values as the
        // yyyy-mm-dd string the input actually needs instead.
        //
        if (urlDefinedValue && param.cfr_code !== 'division_codes')
        {
          param.cfr_default_value = param.data_ind_html_control === 'date' ? toDateInputValue(urlDefinedValue) : urlDefinedValue;
        }
        else if (urlDefinedValue && param.cfr_code === 'division_codes')
        {
          param.cfr_default_value = (param.cfr_default_value as string) + '';
        }
        else if (param.cfr_default_value != null)
        {
          param.cfr_default_value = param.data_ind_html_control === 'date'
            ? toDateInputValue(param.cfr_default_value as string)
            : (param.cfr_default_value as string) + '';
        }

        if ((param.cfr_depends_on == null) && param.cfr_lov_preset != null)
        {
          this.setLovPreset(index, param.cfr_lov_preset, null);
          if (param.cfr_default_value != undefined)
          {
            this.lovSelectionChanged(param);
          }
        }
      });

      shown = true;
    }
    else
    {
      //
      // Reported before the assignment, not after: this is a navigation, and whatever runs after it
      // is running in a document the browser may already be tearing down.
      //
      this.thirdParty.trackDownload(sanitisedDownloadUrl(fn.cfn_preset_name), fn.cfn_preset_name);

      window.location.href = 'Preset?preset=' + fn.cfn_preset_name;
    }

    return shown;
  }

  setLovPreset(index: number, cfrLovPreset: string, cfrDefaultValue: string | null | undefined): void
  {
    let urlPreset = 'Preset?preset=' + cfrLovPreset;
    if (cfrDefaultValue)
    {
      urlPreset += '&reference=' + cfrDefaultValue;
    }

    this.loading.track(this.http.get<GmrLovResponse>(urlPreset)).pipe(
      catchError(error => this.handleFallbackError(error, 'xxgmr115'))
    ).subscribe(response =>
    {
      if (!response) return;

      const data = response.Results.lov.Data;

      //
      // Fatal here too, and not merely because it is the house rule now: `row.code` is the value that
      // gets posted as the report parameter. A miscased `code` fills the dropdown with `undefined`
      // entries that look selectable, and the report then runs on whatever that becomes. An empty
      // dropdown behind an error modal is the better of the two.
      //
      if (!this.verifyContract([['lov', data[0]]]))
      {
        return;
      }

      for (const row of data)
      {
        if (row.code != null)
        {
          row.code = row.code + '';
        }
      }

      const presets = [...this.myLovPresets()];
      presets[index] = data;
      this.myLovPresets.set(presets);
    });
  }

  //
  // Part of the executeHash bookmark restore: division_codes' array value isn't restored through
  // the generic per-parameter hash-restore loop in selectFunction (that loop only ever
  // stringifies cfr_default_value for division_codes, never touches cfr_default_value_arr) - the
  // original's executeHash sets it separately, after triggering the function click.
  //
  restoreDivisionCodes(codes: string[]): void
  {
    const parameters = [...this.selectedFunctionParameters()];
    if (parameters[0])
    {
      parameters[0] = { ...parameters[0], cfr_default_value_arr: codes };
      this.selectedFunctionParameters.set(parameters);
    }
  }

  //
  // 1:1 port of $scope.selectAll: fetches the full divisions list independently of the LOV
  // already loaded for the field (not from myLovPresets) and selects all of it. Always assumes
  // division_codes is myFunctionParameters[0], matching the original.
  //
  // selectAllDivisionsInFlight guards against a double-click firing this GET twice before the
  // first response lands - not present in the original, which had the same dead `arrSet` gate
  // documented in the migration plan (reset on every call, so it never actually debounced
  // anything). A real in-flight guard here is a small, self-contained addition, not a 1:1 port.
  //
  private selectAllDivisionsInFlight = false;

  selectAllDivisions(): void
  {
    if (this.selectAllDivisionsInFlight) return;
    this.selectAllDivisionsInFlight = true;

    this.loading.track(this.http.get<GmrLovResponse>('Preset?preset=gmr-eol-lov-divisions-multi')).pipe(
      catchError(error => this.handleFallbackError(error, 'xxgmr118')),
      finalize(() => { this.selectAllDivisionsInFlight = false; })
    ).subscribe(response =>
    {
      if (!response) return;

      if (!this.verifyContract([['lov', response.Results.lov.Data[0]]]))
      {
        return;
      }

      const codes = response.Results.lov.Data.map(row => row.code);
      const parameters = [...this.selectedFunctionParameters()];
      parameters[0] = { ...parameters[0], cfr_default_value_arr: codes };
      this.selectedFunctionParameters.set(parameters);
    });
  }

  //
  // 1:1 port of $scope.selectDefault.
  //
  selectDefaultDivisions(): void
  {
    const parameters = [...this.selectedFunctionParameters()];
    const divisions = parameters[0];
    parameters[0] = { ...divisions, cfr_default_value_arr: [divisions.cfr_default_value as string] };
    this.selectedFunctionParameters.set(parameters);
  }

  //
  // Both lovSelectionChanged and lovTextChanged cascade to dependent LOVs; lovTextChanged only
  // does so once the free-text value is at least 3 characters, matching the original.
  //
  lovSelectionChanged(parameter: GmrFunctionParameter): void
  {
    this.cascadeDependents(parameter, parameter.cfr_default_value as string | undefined);
  }

  lovTextChanged(parameter: GmrFunctionParameter): void
  {
    const currentValue = parameter.cfr_default_value as string | undefined;
    if (currentValue != null && currentValue.length >= 3)
    {
      this.cascadeDependents(parameter, currentValue);
    }
  }

  private cascadeDependents(parameter: GmrFunctionParameter, referenceValue: string | undefined): void
  {
    for (const dependent of this.myParameters())
    {
      if (dependent.cfr_depends_on === parameter.cfr_code)
      {
        dependent.cfr_default_value = undefined;
        const index = this.myParameters().indexOf(dependent);
        this.setLovPreset(index, dependent.cfr_lov_preset as string, referenceValue);
      }
    }
  }

  private handleFallbackError(error: unknown, msgCode: string)
  {
    //
    // Unconditionally, and regardless of which of the two error paths ends up rendering something: a
    // failed request means the catalog is never coming, so the loading overlay must give way to the
    // error modal rather than sit on top of it.
    //
    this.loading.awaitingCatalog.set(false);

    const httpError = error instanceof HttpErrorResponse ? error : null;

    //
    // Mirrors the `if (!error.data)` guard on every .catch() in the original: the global
    // errorInterceptor already rendered a specific error when the response carried a body: this
    // fallback only fires when there was nothing for it to show (network failure, empty body).
    //
    // ...except for one case that guard gets wrong, fixed 2026-08-14. A 2xx response whose body is
    // not JSON also arrives here as an HttpErrorResponse: HttpClient parses the body, fails, and
    // reports `ok: false` while keeping the ORIGINAL status - so status is 200 and `.error` holds the
    // parse failure `{ error: SyntaxError, text: <body> }`, which is truthy. The guard read that
    // truthiness as "the interceptor has already shown something", but errorInterceptor only reacts
    // to the genuine failure statuses in its own HANDLED_STATUS_CODES and never sees a 200. Net
    // result: no modal, no console error, no redirect - the app sat on "Waiting for data" for ever.
    //
    // That is not a theoretical hole. It is what a gateway interstitial, an expired-session login
    // page or a misrouted request looks like, and it is the exact symptom the dev server produces
    // when its SPA fallback answers /auth with menu.html (see ai/BUILD.md).
    //
    // Keyed on the status range rather than on the shape of `.error`, deliberately: the wrapper
    // object is an HttpClient implementation detail that differs between its XHR and fetch backends,
    // whereas "this response succeeded at the HTTP level yet still reached an error handler" can only
    // mean the body could not be parsed.
    //
    const isBodyParseFailure = httpError != null && httpError.status >= 200 && httpError.status < 300;

    if (!httpError || !httpError.error || isBodyParseFailure)
    {
      this.translate.get(['itgen_gmr_error', 'itgen_gmr_failed_data_retrieval', 'itgen_gmr_reauthenticate', 'itgen_gmr_invalid_answer_from_server']).subscribe(t =>
      {
        if (isBodyParseFailure && httpError)
        {
          this.errorState.showFromParseFailure(httpError, msgCode, t['itgen_gmr_error'], t['itgen_gmr_invalid_answer_from_server']);
          return;
        }

        this.errorState.showFromHttpError(httpError ?? String(error), msgCode, t as any);
      });
    }

    return of(null);
  }
}

//
// 1:1 port of the res:/{/} stripping applied to cfn_description, cfn_documentation and
// cfr_user_hint.
//
function cleanupResourceText(value: string): string
{
  return value.replace(/res:/g, '').replace(/\{|\}|/g, '');
}

//
// Normalizes any ISO-ish date/datetime string to the plain yyyy-mm-dd a native
// <input type="date"> requires - a full timestamp would otherwise be silently rejected.
//
// Exported since 2026-08-14 for the WebMCP `prepare_report` tool, which writes agent-supplied values
// into the very same fields and would otherwise need its own copy of this rule. An agent handed the
// backend's own default (`2026-08-01T00:00:00`) and passing it straight back is the normal case, not
// an edge one, and a date input rejects such a value silently - leaving an empty required field and
// no explanation anywhere.
//
export function toDateInputValue(value: string): string
{
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value);
  return match ? match[0] : value;
}
